import React, { useState, useEffect } from 'react';
import { aiApi } from '../../services/api';
import { 
  MagnifyingGlassIcon, 
  DocumentIcon, 
  ArrowDownTrayIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface Document {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  url: string;
  source: string;
  sourceId?: string;
  status: string;
  metadata?: {
    noticeId?: string;
    solicitationNumber?: string;
    opportunityTitle?: string;
    agency?: string;
    savedFromOpportunity?: boolean;
    savedAt?: string;
  };
  createdAt: string;
}

const Documents: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [error, setError] = useState('');

  // Fetch user's documents on mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setIsLoadingDocs(true);
      const response = await aiApi.getDocuments();
      setDocuments(response.data.data || []);
    } catch (error: any) {
      if (error.response?.status === 403) {
        setError('AI Document Hub requires PRO or ENTERPRISE plan');
      } else {
        setError('Failed to load documents');
      }
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setIsLoading(true);
    try {
      const response = await aiApi.queryDocuments({ query });
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return 'Unknown size';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Filter documents based on search query
  const filteredDocuments = query.trim() 
    ? documents.filter(doc => 
        doc.filename.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.noticeId?.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.solicitationNumber?.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.agency?.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.opportunityTitle?.toLowerCase().includes(query.toLowerCase())
      )
    : documents;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Document Hub</h1>
        <p className="mt-1 text-gray-600">Search and manage your saved documents from opportunities</p>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by filename, notice ID, solicitation number, or agency..."
              className="input pl-10 w-full"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {results.length > 0 && (
          <div className="mt-6 space-y-4">
            {results.map((result, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <DocumentIcon className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-900">{result.filename}</span>
                </div>
                <p className="mt-2 text-sm text-gray-600">{result.excerpt}</p>
                <p className="mt-1 text-xs text-gray-400">
                  Relevance: {(result.relevance * 100).toFixed(0)}%
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Documents */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">My Documents</h2>
          <p className="text-sm text-gray-500 mt-1">
            {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        {isLoadingDocs ? (
          <div className="px-6 py-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4"></div>
            Loading documents...
          </div>
        ) : error ? (
          <div className="px-6 py-8 text-center text-red-600">{error}</div>
        ) : filteredDocuments.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            {query.trim() 
              ? 'No documents match your search' 
              : 'No documents saved yet. Save documents from opportunity pages to see them here.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <DocumentIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      <span>{formatFileSize(doc.size)}</span>
                      <span>•</span>
                      <span>{formatDate(doc.createdAt)}</span>
                      {doc.metadata?.solicitationNumber && (
                        <>
                          <span>•</span>
                          <span className="text-brand-600">{doc.metadata.solicitationNumber}</span>
                        </>
                      )}
                      {doc.status === 'processed' && (
                        <>
                          <span>•</span>
                          <CheckCircleIcon className="h-3 w-3 text-green-500 inline" />
                          <span className="text-green-600">AI Ready</span>
                        </>
                      )}
                    </div>
                    {doc.metadata?.opportunityTitle && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        {doc.metadata.opportunityTitle}
                        {doc.metadata.agency && ` • ${doc.metadata.agency}`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Documents;
